'use client';
import { apiSdkAtom } from '@/context/atoms/ApiSdk';
import { publicConfigAtom } from '@/context/atoms/publicConfig';
import { MessageEvent, type SocketIOEventsMap, SubscriptionPayload } from '@/context/messaging/socket-io';
import createContextHardened from '@/helper/createContextHardened';
import { useAtom } from 'jotai';
import React, { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { io as ioSocket, type Socket } from 'socket.io-client';
import { authMechanismAtom } from '@/context/atoms/AuthMechanism';
import useStableCallback from '@/helper/useStableCallback';
import { WebEamAuthMechanism } from '@/helper/auth/WebEamAuthMechanism';
import { authenticationAtom } from '@/context/atoms/Authentication';

export interface MqttSubscription<T = unknown> {
  payload: SubscriptionPayload;
  eventHandler: (data: T) => void;
  onAcknowledge?: (subscription: SubscriptionPayload) => void;
}

interface SocketIOContextState {
  isConnected: boolean;
  isConnecting: boolean;
  showIndicator: boolean;
  connect: () => void;
  disconnect: () => void;
  subscribe: (key: string, subscription: MqttSubscription) => void;
  unsubscribe: (key: string) => void;
  subscriptions: Record<string, MqttSubscription>;
}

const [SocketIOContext, useSocketIO] = createContextHardened<SocketIOContextState>('SocketIO');
export { useSocketIO };

/** retry delay after a server-side handshake denial, e.g. expired token or backend outage during the auth check */
const DENIAL_RETRY_DELAY_MS = 3000;

/**
 * Interval for renewing all active subscriptions (lease renewal). The renewal
 * is normally silent: the server only acknowledges it when the subscription
 * had been lost server-side and was just restored - exactly then consumers
 * refetch their data to reconcile potentially missed events. A healthy renewal
 * causes no acknowledgement, no refetch and no visible reload.
 */
const SUBSCRIPTION_HEARTBEAT_MS = 60000;

export type SocketIOProviderProps = PropsWithChildren;

export function SocketIOProvider({ children }: SocketIOProviderProps): React.JSX.Element {
  const [api] = useAtom(apiSdkAtom);
  const [publicConfig] = useAtom(publicConfigAtom);
  const [authMechanism] = useAtom(authMechanismAtom);
  const [, setAuthentication] = useAtom(authenticationAtom);

  const authToken = useMemo(() => authMechanism.authentication.authToken, [authMechanism.authentication.authToken]);

  const [isConnecting, startConnecting] = useTransition();
  const [isConnected, setConnected] = useState<boolean>(false);
  const [showIndicator, setShowIndicator] = useState<SocketIOContextState['showIndicator']>(false);
  const [subscriptions, setSubscriptions] = useState<Record<string, MqttSubscription>>({});
  /**
   * Reactive stand-in for the socket instance: the socket lives in a ref, so
   * effects cannot depend on it directly. Incremented once per created socket
   * (normally once per session) - only the change matters, the value is never
   * read.
   */
  const [socketGeneration, setSocketGeneration] = useState<number>(0);

  const socketRef = useRef<Socket<SocketIOEventsMap>>(null);
  /** pending manual retry after a server-side handshake denial (socket.io does not auto-retry those) */
  const denialRetryTimerRef = useRef<number | undefined>(undefined);
  /** guards against overlapping token refreshes while reconnect attempts stack up */
  const refreshInFlightRef = useRef<boolean>(false);

  // refactored to a single socket-io instance. as of now, we don't need multiple socket connections
  const connect = useCallback((): void => {
    setShowIndicator(true);

    console.log('Initiating socket.io connection');

    if (isConnecting) {
      return;
    }

    startConnecting(() => {
      if (socketRef.current !== null) {
        socketRef.current.connect();
      }
    });
  }, [isConnecting]);

  const disconnect = useCallback(() => {
    console.log('Called socket.io disconnet');
    setShowIndicator(false);

    if (socketRef.current !== null) {
      socketRef.current.disconnect();
    }
  }, []);

  const handleBrowserEvent = useStableCallback(
    (eventType: 'visibility' | 'online') => {
      if (eventType === 'visibility' && document.visibilityState !== 'visible') {
        return;
      }

      console.log('Browser Event: ' + eventType);
      if (!isConnected) {
        console.log('Reconnecting via provider (handles token refresh)...');
        connect();
      }
    },
    [isConnected, connect],
  );

  /**
   * Mirror of the subscriptions state, always written together with it. The
   * socket side effects in subscribe/unsubscribe read from this mirror so the
   * state updaters stay pure (react may invoke updaters more than once, and
   * setState calls from within an updater are applied after it, which would
   * make a re-subscribe of an existing key net-remove it).
   */
  const subscriptionsRef = useRef<Record<string, MqttSubscription>>({});

  /** Detaches a subscription's socket listeners and tells the server to stop forwarding. */
  const detachSubscription = useCallback((subscription: MqttSubscription): void => {
    const socket = socketRef.current;

    if (socket === null) {
      return;
    }

    // Emit unsubscribe to server so it stops forwarding MQTT messages
    if (socket.connected) {
      socket.emit(MessageEvent.MQTT_UNSUBSCRIBE, subscription.payload);
    }

    socket.off(subscription.payload.type, subscription.eventHandler);

    if (subscription.onAcknowledge !== undefined) {
      socket.off(MessageEvent.MQTT_SUBSCRIPTION_ACKNOWLEDGED, subscription.onAcknowledge);
    }
  }, []);

  const unsubscribe = useCallback(
    (key: string): void => {
      const subscription = subscriptionsRef.current[key];

      if (subscription === undefined) {
        return;
      }

      // Socket cleanup happens outside the state updater; the registry entry
      // is removed regardless of the socket state.
      detachSubscription(subscription);

      const { [key]: _, ...reduced } = subscriptionsRef.current;
      subscriptionsRef.current = reduced;

      console.log('MQTT subscription removed:', key);

      setSubscriptions((prev) => {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [detachSubscription],
  );

  const subscribe = useCallback(
    (key: string, subscription: MqttSubscription): void => {
      // If the subscription already exists it is replaced: detach the old
      // listeners first, the attach effect below wires up the new ones.
      const existing = subscriptionsRef.current[key];
      if (existing !== undefined) {
        detachSubscription(existing);
      }

      // Pure registration - listener wiring happens in the attach effect once
      // the socket exists (subscribe() may run before the provider created it).
      subscriptionsRef.current = { ...subscriptionsRef.current, [key]: subscription };
      setSubscriptions((prev) => ({ ...prev, [key]: subscription }));
    },
    [detachSubscription],
  );

  // (Re)attach socket listeners for all registered subscriptions. Registering
  // and wiring are separate steps because subscribe() may run before the
  // socket exists (provider and view mounting in the same commit). off()
  // before on() keeps this idempotent.
  useEffect(() => {
    const socket = socketRef.current;

    if (socket === null) {
      return;
    }

    Object.values(subscriptions).forEach((subscription) => {
      // cast via variable to satisfy Socket.IO's type signature
      const handler: (data: unknown) => void = subscription.eventHandler;

      socket.off(subscription.payload.type, handler);
      socket.on(subscription.payload.type, handler);

      if (subscription.onAcknowledge !== undefined) {
        socket.off(MessageEvent.MQTT_SUBSCRIPTION_ACKNOWLEDGED, subscription.onAcknowledge);
        socket.on(MessageEvent.MQTT_SUBSCRIPTION_ACKNOWLEDGED, subscription.onAcknowledge);
      }
    });
  }, [subscriptions, socketGeneration]);

  useEffect(() => {
    const hostname = publicConfig.env.SOCKET_IO_HOST ?? window.location.hostname;
    const socketIoPort = publicConfig.env.SOCKET_IO_PORT ?? 3030;
    const socketIoServer = `//${hostname}:${socketIoPort}`;

    socketRef.current = ioSocket(socketIoServer, {
      // Reconnection is handled by the socket.io manager itself: randomized
      // exponential backoff, retries failed initial attempts as well as lost
      // connections. We only add what the manager cannot do on its own:
      // refreshing an expired auth token (connect_error handler below) and
      // recovering from an explicit server-side disconnect.
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      autoConnect: false,
      // Only the access token string. Never send the full AuthToken object
      // here: it contains the long-lived refresh token, and handshakes get
      // logged server-side on relay errors.
      auth: {
        authToken: authToken?.token,
      },
    });

    setSocketGeneration((generation) => generation + 1);

    socketRef.current.on('connect_error', async (error) => {
      console.log('socket.io connection error');
      console.log('connect_error details:', error);
      setConnected(false);

      // The 401 can arrive as our relayed handshake error ("Handshake failed
      // (401): ...") or as an engine.io TransportError carrying the status in
      // its description.
      const isUnauthorized =
        error?.message?.includes('(401)') === true || ('description' in error && error.description === 401);

      if (isUnauthorized) {
        console.log('Authentication expired, refreshing token...');

        if (authMechanism instanceof WebEamAuthMechanism) {
          // WebEamAuth is unable to refresh the access_token at the time being.
          // So we need to trigger a logout of the full worker ui app, here.
          console.log('WebEamAuthMechanism cannot refresh. Logging out...');
          //await authMechanism.invalidateAuthentication();
          setAuthentication({
            isAuthenticated: false,
            authToken: undefined,
            user: undefined,
          });
          return;
        }

        if (refreshInFlightRef.current) {
          console.log('Token refresh already in progress, skipping duplicate refresh');
        } else {
          refreshInFlightRef.current = true;
          try {
            // Call getCurrentUser() to trigger an HTTP request that will:
            // 1. Be caught by the 401 interceptor
            // 2. Automatically refresh the token
            // 3. Update the auth token atom
            await api.getCurrentUser();

            // Write the fresh token synchronously for the next connection attempt.
            // The authToken effect below also does this, but only after a react
            // render cycle, which can lose the race against the next attempt.
            if (socketRef.current !== null) {
              socketRef.current.auth = {
                authToken: authMechanism.authentication.authToken?.token,
              };
            }
            console.log('Token refreshed, socket auth updated for next reconnect attempt');
          } catch (refreshError) {
            console.log('Token refresh failed, will retry on next connection attempt:', refreshError);
          } finally {
            refreshInFlightRef.current = false;
          }
        }
      }

      // Transport-level failures are retried by the manager itself with
      // backoff. A handshake denial from the server middleware ("Handshake
      // failed (401/500): ...") instead closes the socket for good
      // (socket.active === false) and socket.io deliberately does not retry
      // those - so we have to. Delayed, to not hammer the server while the
      // cause (expired token, backend outage during the auth check) persists.
      if (socketRef.current !== null && !socketRef.current.active && denialRetryTimerRef.current === undefined) {
        console.log(`socket.io connection denied by server, retrying in ${DENIAL_RETRY_DELAY_MS}ms`);
        denialRetryTimerRef.current = window.setTimeout(() => {
          denialRetryTimerRef.current = undefined;
          if (socketRef.current !== null && !socketRef.current.connected) {
            socketRef.current.connect();
          }
        }, DENIAL_RETRY_DELAY_MS);
      }
    });

    socketRef.current.on('connect', () => {
      console.log('socket.io connected');
      setConnected(true);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('socket.io disconnected:', reason);
      setConnected(false);

      // Transport-level disconnects (transport close, ping timeout, ...) are
      // reconnected by the manager itself. 'io server disconnect' is the one
      // reason it deliberately does not recover from, and 'io client
      // disconnect' means we called disconnect() intentionally.
      if (reason === 'io server disconnect' && socketRef.current !== null) {
        console.log(`Reconnecting after server-side disconnect in ${DENIAL_RETRY_DELAY_MS}ms`);
        window.setTimeout(() => {
          if (socketRef.current !== null && !socketRef.current.connected) {
            socketRef.current.connect();
          }
        }, DENIAL_RETRY_DELAY_MS);
      }
    });

    connect();

    // Event listeners use stableHandleBrowserEvent which always calls the latest callback
    const onVisibilityChange = (): void => handleBrowserEvent('visibility');
    const onOnline = (): void => handleBrowserEvent('online');

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      if (denialRetryTimerRef.current !== undefined) {
        window.clearTimeout(denialRetryTimerRef.current);
        denialRetryTimerRef.current = undefined;
      }
      if (socketRef.current !== null) {
        socketRef.current.disconnect();
        socketRef.current.removeAllListeners();
        socketRef.current = null;
      }
    };
    /** This hook has to run exactly once on-mount. Therefore no dependencies! */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (socketRef.current !== null) {
      socketRef.current.auth = {
        authToken: authToken?.token,
      };

      console.log('socket.io auth token replaced');
    }
  }, [authToken]);

  // Resubscribe whenever subscriptions change or socket connects
  useEffect(() => {
    if (isConnected && socketRef.current?.connected && Object.keys(subscriptions).length !== 0) {
      console.log('Resubscribing due to subscription or connection change:', subscriptions);

      Object.entries(subscriptions).forEach(([key, subscription]) => {
        if (socketRef.current === null) {
          return;
        }
        console.log('Emitting subscription for:', key);
        socketRef.current.emit(MessageEvent.MQTT_SUBSCRIBE, subscription.payload);
      });
    }
  }, [subscriptions, isConnected]);

  // Periodically re-assert all subscriptions. The client cannot detect
  // server-side subscription loss (server or mqtt restarts keep the socket
  // healthy), so subscriptions are treated as leases that get renewed instead
  // of one-shot registrations.
  useEffect(() => {
    if (!isConnected || Object.keys(subscriptions).length === 0) {
      return;
    }

    const heartbeatInterval = window.setInterval(() => {
      if (socketRef.current === null || !socketRef.current.connected) {
        return;
      }

      Object.entries(subscriptions).forEach(([key, subscription]) => {
        console.log('Heartbeat subscription renewal for:', key);
        socketRef.current?.emit(MessageEvent.MQTT_SUBSCRIBE_RENEW, subscription.payload);
      });
    }, SUBSCRIPTION_HEARTBEAT_MS);

    return () => {
      window.clearInterval(heartbeatInterval);
    };
  }, [subscriptions, isConnected]);

  return (
    <SocketIOContext.Provider
      value={{
        isConnected,
        isConnecting,
        showIndicator,
        connect,
        disconnect,
        subscribe,
        unsubscribe,
        subscriptions,
      }}
    >
      {children}
    </SocketIOContext.Provider>
  );
}
